import { Component, h, Prop } from '@stencil/core';
import { __ } from '@wordpress/i18n';

import { BankAccount, PaymentInstrument, PaymentMethod } from '../../../types';
import { getRazorpayMethodIcon, getRazorpayMethodLabel } from '../../../functions/razorpay';

@Component({
  tag: 'sc-payment-method',
  styleUrl: 'sc-payment-method.css',
  shadow: true,
})
export class ScPaymentMethod {
  @Prop() paymentMethod: PaymentMethod;
  @Prop() full: boolean;
  @Prop() externalLink: string;
  @Prop() externalLinkTooltipText: string;

  renderBankAccountType(type) {
    if (type === 'checking') {
      return __('Checking', 'surecart');
    }
    if (type === 'savings') {
      return __('Savings', 'surecart');
    }
  }

  renderExternalLink() {
    return (
      !!this.externalLink && (
        <sc-tooltip text={this.externalLinkTooltipText} type="text">
          <sc-button style={{ color: 'var(--sc-color-gray-500)' }} type="text" size="small" href={this.externalLink} target="_blank">
            <sc-icon name="external-link" style={{ fontSize: '16px' }}></sc-icon>
          </sc-button>
        </sc-tooltip>
      )
    );
  }

  render() {
    if ((this.paymentMethod?.bank_account as BankAccount)?.id) {
      const account = this.paymentMethod?.bank_account as BankAccount;
      return (
        <div class="payment-method" part="bank">
          <span>{this.renderBankAccountType(account?.account_type)}</span>
          **** {account?.last4}
          {this.renderExternalLink()}
        </div>
      );
    }

    if ((this?.paymentMethod?.payment_instrument as PaymentInstrument)?.instrument_type) {
      const type = (this?.paymentMethod?.payment_instrument as PaymentInstrument)?.instrument_type;
      if (!!this?.paymentMethod?.payment_method_name) {
        return (
          <div class="payment-method" part="instrument">
            <div class="payment-method-icon">
              <sc-icon style={{ 'fontSize': '36px', '--height': '0.63em' }} name={type} />
            </div>
            <span>{this?.paymentMethod?.payment_method_name}</span>
            {this.renderExternalLink()}
          </div>
        );
      }

      if (type === 'paypal') {
        return (
          <div class="payment-method" part="instrument">
            <sc-icon style={{ fontSize: '56px', lineHeight: '1', height: '28px' }} name="paypal"></sc-icon>
          </div>
        );
      }

      // Razorpay method types — explicit label/icon so acronyms stay uppercase ("UPI", not "Upi").
      const razorpayLabel = getRazorpayMethodLabel(type);
      if (razorpayLabel) {
        return (
          <div class="payment-method" part="instrument">
            <sc-icon style={{ fontSize: '24px' }} name={getRazorpayMethodIcon(type)} aria-hidden="true"></sc-icon>
            <span>{razorpayLabel}</span>
            {this.renderExternalLink()}
          </div>
        );
      }

      return (
        <div class="payment-method" part="instrument">
          <sc-tag exportparts="base:payment_instrument" type="info" pill>
            <span style={{ textTransform: 'capitalize' }}>{type} </span>
          </sc-tag>
          {this.renderExternalLink()}
        </div>
      );
    }

    if (this.paymentMethod?.card?.brand) {
      return (
        <div class="payment-method" part="card">
          <sc-cc-logo style={{ fontSize: '36px' }} brand={this.paymentMethod?.card?.brand}></sc-cc-logo>
          <sc-text style={{ whiteSpace: 'nowrap', paddingRight: '6px' }}>**** {this.paymentMethod?.card?.last4}</sc-text>
          {this.renderExternalLink()}
        </div>
      );
    }

    if (this.paymentMethod?.paypal_account?.id) {
      return (
        <div class="payment-method" part="base" style={{ gap: 'var(--sc-spacing-small)' }}>
          <sc-icon style={{ fontSize: '56px', lineHeight: '1', height: '28px' }} name="paypal"></sc-icon>
          {this.full && (
            <sc-text style={{ '--font-size': 'var(--sc-font-size-small)' }} truncate>
              {this.paymentMethod?.paypal_account?.email}
            </sc-text>
          )}
          {this.renderExternalLink()}
        </div>
      );
    }

    return this?.paymentMethod?.processor_type;
  }
}
